import React from 'react';
import { X, Package, Plus, Minus, Check, Tag, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/languages/LanguageContext';
import { useProducts } from '../../../contexts/backend/ProductsContext';

interface CartItem {
  id: string;
  name: string;
  price: number;
  currency: string;
  type: string;
  quantity: number;
  [key: string]: any;
}

interface CartListProps {
  items: CartItem[];
  onRemoveItem: (itemId: string) => void;
  onUpdateQuantity?: (itemId: string, quantity: number) => void;
  tradingSystem?: 'rithmic' | 'dxfeed' | '';
  onTradingSystemChange?: (system: 'rithmic' | 'dxfeed') => void;
  selectedCurrency?: 'EUR' | 'USD';
}

const CartList: React.FC<CartListProps> = ({
  items,
  onRemoveItem,
  onUpdateQuantity,
  tradingSystem = '',
  onTradingSystemChange,
  selectedCurrency = 'EUR'
}) => {
  const { t, language } = useLanguage();
  const { getProductById } = useProducts();
  const currencySymbol = selectedCurrency === 'EUR' ? '€' : '$';

  const formatPrice = (value: number | undefined | null): string => {
    if (value == null) return '0';
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2);
  };

  const getItemPrice = (item: CartItem) => {
    const product = getProductById(item.id);
    if (!product?.prices) return item.price;
    const priceData = product.prices.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return item.price;

    const volumeDiscount = product.volume_discounts?.find(vd => vd.currency_code === selectedCurrency);
    if (volumeDiscount?.tiers?.length) {
      const applicableTier = [...volumeDiscount.tiers]
        .sort((a, b) => b.min_qty - a.min_qty)
        .find(tier => item.quantity >= tier.min_qty);
      if (applicableTier) {
        return applicableTier.discounted_price;
      }
    }

    const hasDiscount = priceData.discounted_price !== null && priceData.discounted_price !== priceData.price;
    return hasDiscount ? (priceData.discounted_price || priceData.price) : priceData.price;
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const isExtension = item.type === 'Extension' || item.type === 'FundedFeeExtension' || item.category === 'Extension';
      const quantity = (item.isMultiReset || isExtension) ? 1 : item.quantity;
      const price = getItemPrice(item);
      return total + (price || 0) * quantity;
    }, 0);
  };

  const getProductDiscount = (item: CartItem) => {
    const product = getProductById(item.id);
    if (!product?.prices) return null;
    const priceData = product.prices.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return null;

    const volumeDiscount = product.volume_discounts?.find(vd => vd.currency_code === selectedCurrency);
    if (volumeDiscount?.tiers?.length) {
      const applicableTier = [...volumeDiscount.tiers]
        .sort((a, b) => b.min_qty - a.min_qty)
        .find(tier => item.quantity >= tier.min_qty);
      if (applicableTier) {
        return {
          originalPrice: priceData.price,
          discountedPrice: applicableTier.discounted_price
        };
      }
    }

    const hasDiscount = priceData.discounted_price !== null && priceData.discounted_price !== priceData.price;
    if (!hasDiscount) return null;
    return {
      originalPrice: priceData.price,
      discountedPrice: priceData.discounted_price || priceData.price
    };
  };

  const getActiveVolumeDiscount = (item: CartItem) => {
    const product = getProductById(item.id);
    if (!product?.volume_discounts?.length) return null;
    const volumeDiscount = product.volume_discounts.find(vd => vd.currency_code === selectedCurrency);
    if (!volumeDiscount?.tiers?.length) return null;
    const priceData = product.prices?.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return null;
    const applicableTier = [...volumeDiscount.tiers]
      .sort((a, b) => b.min_qty - a.min_qty)
      .find(tier => item.quantity >= tier.min_qty);
    if (!applicableTier) return null;
    const savingsPerUnit = priceData.price - applicableTier.discounted_price;
    const percentage = Math.round((savingsPerUnit / priceData.price) * 100);
    return { savingsPerUnit, totalSavings: savingsPerUnit * item.quantity, percentage };
  };

  const getNextVolumeTier = (item: CartItem) => {
    const product = getProductById(item.id);
    if (!product?.volume_discounts?.length) return null;
    const volumeDiscount = product.volume_discounts.find(vd => vd.currency_code === selectedCurrency);
    if (!volumeDiscount?.tiers?.length) return null;
    const priceData = product.prices?.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return null;
    const nextTier = [...volumeDiscount.tiers]
      .sort((a, b) => a.min_qty - b.min_qty)
      .find(tier => item.quantity < tier.min_qty);
    if (!nextTier) return null;
    const percentage = Math.round(((priceData.price - nextTier.discounted_price) / priceData.price) * 100);
    return { minQty: nextTier.min_qty, percentage };
  };

  const hasAccountProducts = items.some(item => item.type === 'Account');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-xl flex items-center justify-center border border-amber-500/30">
          <Package className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">{t.purchaseModal?.cart || 'Your Cart'}</h3>
          <p className="text-xs text-slate-400">{items.length} {items.length === 1 ? 'item' : 'items'}</p>
        </div>
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {items.map((item, index) => {
          const nameParts = item.name.split(' ');
          const sizeString = nameParts[1] || '';
          const isMultiReset = item.isMultiReset === true;
          const isExtension = item.type === 'Extension' || item.type === 'FundedFeeExtension' || item.category === 'Extension';
          const hasAccountNumber = (isMultiReset || isExtension) && item.accountNumber;

          // Determine display text for the badge
          let badgeText = sizeString;
          if (isMultiReset) {
            badgeText = 'RESET';
          } else if (isExtension) {
            badgeText = 'EXT';
          }

          return (
            <div
              key={hasAccountNumber ? `${item.id}-${item.accountNumber}-${index}` : item.id}
              className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-4 border border-slate-700/50 hover:border-slate-600/50 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                    <div className="absolute inset-0">
                      <div className="absolute top-1/4 left-1/4 w-12 h-12 bg-gradient-to-br from-yellow-500/20 via-amber-500/15 to-orange-500/10 rounded-full blur-2xl"></div>
                    </div>
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(251,191,36,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(251,191,36,0.03)_1px,transparent_1px)] bg-[size:10px_10px]"></div>
                  </div>
                  <div className="relative z-10 h-full flex items-center justify-center">
                    <div className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-amber-100 to-yellow-200">
                      {badgeText}
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-white truncate pr-2">{item.name}</h4>
                      <p className="text-[10px] text-slate-400 mt-0.5 truncate pr-2">
                        {hasAccountNumber ? item.accountNumber : 'New Evaluation'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const itemKey = hasAccountNumber ? `${item.id}-${item.accountNumber}` : item.id;
                        onRemoveItem(itemKey);
                      }}
                      className="flex-shrink-0 w-7 h-7 bg-slate-700/50 hover:bg-red-500/20 border border-slate-600/50 hover:border-red-500/50 rounded-lg flex items-center justify-center transition-all group/remove"
                    >
                      <X className="w-4 h-4 text-slate-400 group-hover/remove:text-red-400 transition-colors" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col items-start gap-0.5">
                      {(() => {
                        const effectiveQuantity = (isMultiReset || isExtension) ? 1 : item.quantity;
                        const discount = getProductDiscount(item);
                        const itemPrice = getItemPrice(item);
                        if (discount) {
                          return (
                            <>
                              <div className="text-xs font-medium text-slate-600 line-through">
                                {currencySymbol}{formatPrice(discount.originalPrice * effectiveQuantity)}
                              </div>
                              <div className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500">
                                {currencySymbol}{formatPrice(itemPrice * effectiveQuantity)}
                              </div>
                            </>
                          );
                        }
                        return (
                          <div className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500">
                            {currencySymbol}{formatPrice(itemPrice * effectiveQuantity)}
                          </div>
                        );
                      })()}
                    </div>

                    {onUpdateQuantity && !isMultiReset && !isExtension && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="w-7 h-7 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/50 hover:border-slate-500/50 rounded-lg flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700/50"
                        >
                          <Minus className="w-3.5 h-3.5 text-slate-300" />
                        </button>
                        <span className="text-sm font-bold text-white min-w-[24px] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= 10}
                          className="w-7 h-7 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/50 hover:border-slate-500/50 rounded-lg flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700/50"
                        >
                          <Plus className="w-3.5 h-3.5 text-slate-300" />
                        </button>
                      </div>
                    )}
                  </div>

                  {(() => {
                    if (isMultiReset || isExtension) return null;
                    const activeVolume = getActiveVolumeDiscount(item);
                    const nextTier = getNextVolumeTier(item);
                    if (activeVolume) {
                      return (
                        <div className="mt-2 flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
                          <Tag className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                          <span className="text-[10px] text-emerald-300 font-medium">
                            {(t.purchaseModal?.volumeDiscountApplied || 'Volume discount applied ({percentage}% off)')
                              .replace('{percentage}', String(activeVolume.percentage))}
                            {' '}&middot;{' '}
                            {(t.purchaseModal?.volumeDiscountSavings || 'You save {symbol}{amount} per unit')
                              .replace('{symbol}', currencySymbol)
                              .replace('{amount}', activeVolume.savingsPerUnit.toFixed(2))}
                          </span>
                        </div>
                      );
                    }
                    if (nextTier) {
                      return (
                        <div className="mt-2 flex items-center gap-1.5 bg-slate-700/30 border border-slate-600/30 rounded-lg px-2.5 py-1.5">
                          <Tag className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span className="text-[10px] text-slate-400">
                            {language === 'de'
                              ? `Ab ${nextTier.minQty} Stück: ${nextTier.percentage}% Rabatt`
                              : `Buy ${nextTier.minQty}+ for ${nextTier.percentage}% off`}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-700/50 pt-4 mt-4">
        <div className="flex items-center justify-between">
          <span className="text-base font-bold text-white">{t.purchaseModal?.subtotal || 'Subtotal'}</span>
          <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500">
            {currencySymbol}{getTotalPrice().toFixed(2)}
          </span>
        </div>
      </div>

      {/* Trading System Selection - Only for Account products */}
      {hasAccountProducts && onTradingSystemChange && (
        <div className="border-t border-slate-700/50 pt-4">
          <label className={`block text-[10px] font-medium mb-2 uppercase tracking-wider ${
            !tradingSystem ? 'text-amber-400' : 'text-slate-400'
          }`}>
            {t.purchaseModal?.tradingSystem || 'Trading System'}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {/* Rithmic Option */}
            <button
              type="button"
              onClick={() => onTradingSystemChange('rithmic')}
              className={`relative p-2.5 rounded-lg border transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${
                tradingSystem === 'rithmic'
                  ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                  : !tradingSystem
                    ? 'border-amber-500/40 bg-slate-800/50 hover:border-amber-500 hover:bg-slate-800/70 animate-pulse'
                    : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70'
              }`}
            >
              {tradingSystem === 'rithmic' && (
                <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                  <Check className="w-2 h-2 text-slate-900" />
                </div>
              )}
              <img
                src="/images/datafeeds/rithmic logo.png"
                alt="Rithmic"
                className="h-5 w-auto object-contain"
              />
              <span className={`text-[10px] font-medium ${
                tradingSystem === 'rithmic' ? 'text-amber-400' : 'text-slate-300'
              }`}>
                Rithmic
              </span>
            </button>

            {/* DXFeed Option */}
            <button
              type="button"
              onClick={() => onTradingSystemChange('dxfeed')}
              className={`relative p-2.5 rounded-lg border transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${
                tradingSystem === 'dxfeed'
                  ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                  : !tradingSystem
                    ? 'border-amber-500/40 bg-slate-800/50 hover:border-amber-500 hover:bg-slate-800/70 animate-pulse'
                    : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70'
              }`}
            >
              {tradingSystem === 'dxfeed' && (
                <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                  <Check className="w-2 h-2 text-slate-900" />
                </div>
              )}
              <img
                src="/images/datafeeds/dxfeed logo.png"
                alt="DXFeed"
                className="h-5 w-auto object-contain"
              />
              <span className={`text-[10px] font-medium ${
                tradingSystem === 'dxfeed' ? 'text-amber-400' : 'text-slate-300'
              }`}>
                DXFeed
              </span>
            </button>
          </div>
          {!tradingSystem && (
            <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {t.purchaseModal?.selectTradingSystem || 'Please select a data feed'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CartList;
